import {
  Badge,
  Box,
  Card,
  Heading,
  Icon,
  Image,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { LuArrowRight, LuNetwork, LuUsers } from 'react-icons/lu'

import { useAuth } from '@/auth/useAuth'
import { InvitedCommunitiesList } from '@/components/views/accountManagement/registerWizard/steps/congratulations/InvitedCommunitiesList'
import {
  type ProfileConfig,
  type ProfileKey,
  PROFILES,
} from '@/components/views/accountManagement/registerWizard/steps/congratulations/profiles/profilesConfig'
import { useInvitedCommunities } from '@/components/views/accountManagement/registerWizard/steps/congratulations/useInvitedCommunities'

import { PROFILE_GRADIENT } from './profileGradients'

interface ProfileSelectionScreenProps {
  onSelect: (key: ProfileKey) => void
}

/** Single selectable profile hero-card. */
const ProfileCard = ({
  profile,
  onSelect,
}: {
  profile: ProfileConfig
  onSelect: (key: ProfileKey) => void
}) => {
  const { t } = useTranslation()

  const handleSelect = () => onSelect(profile.key)

  return (
    <Card.Root
      role="button"
      tabIndex={0}
      aria-label={t(`${profile.i18nKey}.cardTitle`)}
      onClick={handleSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleSelect()
        }
      }}
      colorPalette={profile.colorPalette}
      overflow="hidden"
      cursor="pointer"
      transition="transform 0.15s, box-shadow 0.15s"
      _hover={{ transform: 'translateY(-4px)', shadow: 'xl' }}
      _focusVisible={{
        outline: '3px solid',
        outlineColor: 'colorPalette.focusRing',
        outlineOffset: '2px',
      }}
    >
      {/* Hero: brand gradient + large icon (image slot wired but optional). */}
      <Box
        position="relative"
        h={{ base: '140px', md: '180px' }}
        bgImage={
          profile.image ? undefined : PROFILE_GRADIENT[profile.colorPalette]
        }
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {profile.image && (
          <Image
            src={profile.image}
            alt=""
            position="absolute"
            inset="0"
            w="full"
            h="full"
            objectFit="cover"
          />
        )}
        <Icon
          as={profile.icon}
          boxSize={{ base: '12', md: '16' }}
          color="white"
          position="relative"
          filter="drop-shadow(0 2px 6px rgba(0,0,0,0.25))"
        />
        <Badge
          position="absolute"
          top="3"
          insetEnd="3"
          colorPalette={profile.colorPalette}
          variant="solid"
        >
          {t(`${profile.i18nKey}.badge`)}
        </Badge>
      </Box>

      <Card.Body gap="2">
        <Heading size="lg">{t(`${profile.i18nKey}.cardTitle`)}</Heading>
        <Text color="fg.muted" textStyle="sm">
          {t(`${profile.i18nKey}.cardDescription`)}
        </Text>
      </Card.Body>
      <Card.Footer>
        <Text
          color="colorPalette.fg"
          fontWeight="semibold"
          display="inline-flex"
          alignItems="center"
          gap="1"
        >
          {t('register.wizard.profiles.discoverCta')}
          <LuArrowRight />
        </Text>
      </Card.Footer>
    </Card.Root>
  )
}

/** Short pedagogical card explaining "community" vs "network". */
const ConceptCard = ({
  icon,
  colorPalette,
  title,
  description,
}: {
  icon: typeof LuUsers
  colorPalette: 'blue' | 'pink'
  title: string
  description: string
}) => (
  <Card.Root colorPalette={colorPalette} variant="subtle" flex="1">
    <Card.Body flexDirection="row" gap="3" alignItems="flex-start">
      <Icon
        as={icon}
        boxSize="6"
        color="colorPalette.fg"
        mt="0.5"
        flexShrink={0}
      />
      <Stack gap="1">
        <Heading size="sm" color="colorPalette.fg">
          {title}
        </Heading>
        <Text textStyle="sm" color="fg.muted">
          {description}
        </Text>
      </Stack>
    </Card.Body>
  </Card.Root>
)

/**
 * First (forced) screen: welcomes the user and lets them pick one of three
 * profiles via prominent hero-cards, with a pedagogical band explaining the
 * community / network concepts. Replaces the legacy diptych selection step.
 */
export const ProfileSelectionScreen = ({
  onSelect,
}: ProfileSelectionScreenProps) => {
  const { t } = useTranslation()
  const auth = useAuth()
  const invited = useInvitedCommunities()

  const firstname = auth.user?.firstname || ''

  // Render the firstname inside the title with a brand gradient. Splitting the
  // translated string keeps the localized phrasing/punctuation around the name.
  const title = t('register.wizard.profiles.title', {
    firstname,
    context: auth.user?.gender,
  })
  const nameIndex = firstname ? title.indexOf(firstname) : -1

  return (
    <Stack gap="10" w="full" maxW="6xl" mx="auto" py={{ base: '6', md: '10' }}>
      <Stack gap="2" textAlign="center" align="center">
        <Heading size={{ base: '2xl', md: '4xl' }}>
          {nameIndex === -1 ? (
            title
          ) : (
            <>
              {title.slice(0, nameIndex)}
              <Text
                as="span"
                bgGradient="to-r"
                gradientFrom="orange.400"
                gradientTo="pink.500"
                bgClip="text"
              >
                {firstname}
              </Text>
              {title.slice(nameIndex + firstname.length)}
            </>
          )}
        </Heading>
        <Text color="fg.muted" textStyle={{ base: 'md', md: 'lg' }} maxW="2xl">
          {t('register.wizard.profiles.subtitle')}
        </Text>
        <Text
          color="fg.muted"
          textStyle="sm"
          maxW="2xl"
          bg="bg.subtle"
          rounded="full"
          px="4"
          py="2"
        >
          {t('register.wizard.profiles.cumulativeHint')}
        </Text>
      </Stack>

      <SimpleGrid columns={{ base: 1, md: 3 }} gap={{ base: 4, md: 6 }}>
        {PROFILES.map((profile) => (
          <ProfileCard
            key={profile.key}
            profile={profile}
            onSelect={onSelect}
          />
        ))}
      </SimpleGrid>

      <InvitedCommunitiesList invited={invited} />

      <Stack
        direction={{ base: 'column', md: 'row' }}
        gap="4"
        bg="bg.subtle"
        p={{ base: '4', md: '6' }}
        rounded="2xl"
      >
        <ConceptCard
          icon={LuUsers}
          colorPalette="blue"
          title={t('register.wizard.profiles.explainer.community.title')}
          description={t(
            'register.wizard.profiles.explainer.community.description'
          )}
        />
        <ConceptCard
          icon={LuNetwork}
          colorPalette="pink"
          title={t('register.wizard.profiles.explainer.network.title')}
          description={t(
            'register.wizard.profiles.explainer.network.description'
          )}
        />
      </Stack>
    </Stack>
  )
}
